import { useMutation } from '@tanstack/react-query';
import { API_BASE_URL } from '@/lib/constants';
import type { EventCategory } from '@/lib/types';
import { useToast } from '@/hooks/useToast';
import { track } from '@/lib/analytics';

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function useExportICS(weddingId: string, tags: EventCategory[]) {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const tagStr = tags.length > 0 ? tags.join(',') : '';
      const url = tagStr
        ? `${API_BASE_URL}/api/weddings/${weddingId}/export/ics?tags=${tagStr}`
        : `${API_BASE_URL}/api/weddings/${weddingId}/export/ics`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      triggerDownload(blob, 'wedding-timeline.ics');
    },
    onSuccess: () => {
      track('export_generated', { format: 'ics' });
      toast('Downloaded!', 'success');
    },
    onError: () => toast('Export failed. Please try again.', 'error'),
  });
}

export function useExportCSV(weddingId: string, tags: EventCategory[]) {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const tagStr = tags.length > 0 ? tags.join(',') : '';
      const url = tagStr
        ? `${API_BASE_URL}/api/weddings/${weddingId}/export/csv?tags=${tagStr}`
        : `${API_BASE_URL}/api/weddings/${weddingId}/export/csv`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      triggerDownload(blob, 'wedding-timeline.csv');
    },
    onSuccess: () => {
      track('export_generated', { format: 'csv' });
      toast('Downloaded!', 'success');
    },
    onError: () => toast('Export failed. Please try again.', 'error'),
  });
}
